import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronDown } from 'lucide-react';

const ReferenceLookupModal = ({
  isOpen,
  onClose,
  onSelect,
  title = 'Records',
  data = [],
  displayKey = 'name',
  columns = [], // Array of { key, label, width } for table columns
  searchKeys = ['name'], // Fallback for searchKeys if columns not provided
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);

  // Default columns if not provided (ServiceNow style)
  const gridColumns = useMemo(() => {
    if (columns.length > 0) return columns;
    // If no columns provided, create default from displayKey
    return [
      { key: displayKey, label: 'Name', width: '35%' },
      { key: 'email', label: 'Email', width: '30%' },
      { key: 'department', label: 'Department', width: '20%' },
      { key: 'job_title', label: 'Title', width: '15%' }
    ];
  }, [columns, displayKey]);

  // "Go to" functionality - default to first column
  const [searchField, setSearchField] = useState(gridColumns[0]?.key || displayKey);

  // Filter data based on specific field search (ServiceNow "Go to" style)
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) {
      return data;
    }

    const queryLower = searchQuery.toLowerCase();
    return data.filter(item => {
      const value = item[searchField];
      return value && value.toString().toLowerCase().includes(queryLower);
    });
  }, [data, searchQuery, searchField]);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSearchField(gridColumns[0]?.key || displayKey);
      // Auto-focus search input when opening
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, gridColumns, displayKey]);

  // Handle click outside and Escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelect = (item) => {
    onSelect(item);
    onClose();
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[9998] backdrop-blur-[1px]" />
      
      {/* Modal */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          ref={modalRef}
          className="bg-white dark:bg-slate-900 rounded-xl md:rounded-lg shadow-2xl border border-gray-300 dark:border-slate-700 w-full max-w-[calc(100vw-2rem)] md:max-w-5xl max-h-[90vh] md:h-[80vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-slate-800 border-b border-gray-300 dark:border-slate-700">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white transition-colors p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar ("Go to" style) */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-300 dark:border-slate-700 flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Go to
            </span>
            
            {/* Field Selector */}
            <div className="relative">
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="appearance-none h-8 pl-3 pr-8 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded text-sm text-gray-700 dark:text-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer"
              >
                {gridColumns.map(col => (
                  <option key={col.key} value={col.key}>{col.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 dark:text-gray-400 pointer-events-none" />
            </div>

            {/* Search Input */}
            <div className="relative flex-1 max-w-xs">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full h-8 pl-3 pr-8 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filteredData.length > 0) {
                    e.preventDefault();
                    handleSelect(filteredData[0]);
                  }
                }}
              />
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
            
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Data Table */}
          <div className="flex-1 overflow-auto bg-white dark:bg-slate-900">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-100 dark:bg-slate-800 sticky top-0 z-10 shadow-sm">
                <tr>
                  {gridColumns.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-slate-700 border-r last:border-r-0 border-gray-200 dark:border-slate-800 whitespace-nowrap"
                      style={{ width: col.width }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={gridColumns.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      {searchQuery ? 'No records found' : 'No records available'}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr
                      key={item.id || index}
                      onClick={() => handleSelect(item)}
                      className="group cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors even:bg-gray-50/50 dark:even:bg-slate-800/30"
                    >
                      {gridColumns.map((col, colIndex) => {
                        const val = item[col.key] || '';
                        // Highlight first column like a link (ServiceNow style)
                        const isLink = colIndex === 0; 
                        
                        return (
                          <td
                            key={col.key}
                            className={`px-4 py-2 border-r last:border-r-0 border-gray-100 dark:border-slate-800 truncate max-w-[200px] ${
                              isLink 
                                ? 'text-blue-600 dark:text-blue-400 font-medium hover:underline' 
                                : 'text-gray-600 dark:text-gray-300'
                            }`}
                            title={val}
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-slate-800 border-t border-gray-300 dark:border-slate-700 flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
            <div>
              Row 1 - {filteredData.length} of {data.length}
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors text-gray-700 dark:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ReferenceLookupModal;
